// PRFAQSection.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const PRFAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'What is the difference between Express Entry and Provincial Nominee Programs?',
      answer: 'Express Entry is a federal program that processes applications in 6 months, while PNPs are province-specific programs that take 18-24 months. Express Entry uses a points-based system (CRS), while PNPs have varying criteria by province. A provincial nomination adds 600 points to your CRS score, virtually guaranteeing an invitation to apply through Express Entry.'
    },
    {
      question: 'Do I need a job offer to apply for Canadian PR?',
      answer: 'Not necessarily. For Express Entry Federal Skilled Worker and Canadian Experience Class programs, a job offer is not required. However, having a valid job offer can significantly boost your CRS score. Some PNP streams require a job offer, while others do not. Family sponsorship does not require a job offer.'
    },
    {
      question: 'What is a CRS score and how is it calculated?',
      answer: 'The Comprehensive Ranking System (CRS) score is used for Express Entry applications. It\'s calculated based on factors including age (maximum points at 20-29 years), education level, language proficiency (English/French), work experience, and additional factors like Canadian education or work experience. The maximum score is 1,200 points, with 600 for core factors and 600 for additional factors including provincial nomination.'
    },
    {
      question: 'Can I include my spouse and children in my PR application?',
      answer: 'Yes, you can include your spouse or common-law partner and dependent children in your PR application. They will also receive permanent resident status. Including your spouse can add up to 40 additional points to your CRS score if they have good language skills, education, or Canadian work experience. Each dependent child requires an additional government fee of CAD $230.'
    },
    {
      question: 'What language tests are accepted for Canadian PR?',
      answer: 'For English: IELTS General Training or CELPIP General. For French: TEF Canada or TCF Canada. Test results must be less than 2 years old at the time of application. You need minimum CLB 7 (IELTS 6.0 equivalent) for Express Entry. Higher language scores significantly improve your CRS score and chances of receiving an invitation to apply.'
    },
    {
      question: 'Do I need an Educational Credential Assessment (ECA)?',
      answer: 'Yes, if you completed your education outside Canada and are applying through Express Entry or most PNP streams, you need an ECA. This assessment verifies that your foreign degree, diploma, or certificate is valid and equal to a Canadian credential. Designated organizations like WES, IQAS, or ICES can provide ECAs. The process typically takes 4-8 weeks.'
    },
    {
      question: 'What happens after I receive my PR?',
      answer: 'After receiving your Confirmation of Permanent Residence (COPR), you must land in Canada before the expiry date on your COPR. Upon landing, you\'ll receive your PR card, which is valid for 5 years. As a PR, you can live, work, or study anywhere in Canada, access healthcare, and apply for citizenship after meeting residency requirements (typically 3 years within a 5-year period).'
    },
    {
      question: 'How long is my PR status valid?',
      answer: 'Your PR status doesn\'t expire, but your PR card is valid for 5 years. To maintain PR status, you must be physically present in Canada for at least 730 days (2 years) within every 5-year period. You can renew your PR card before it expires. After 3 years of physical presence, you can apply for Canadian citizenship.'
    },
    {
      question: 'Can I work while my PR application is being processed?',
      answer: 'If you\'re already in Canada on a valid work permit, you can continue working while your PR application is processed. If you\'re outside Canada, you cannot work until you receive PR status and land in Canada. Some applicants may be eligible for bridging open work permits if their current work permit is expiring while their PR application is in process.'
    },
    {
      question: 'What is the success rate for PR applications?',
      answer: 'Success rates vary by program and individual circumstances. Express Entry has high approval rates (over 80%) for complete applications meeting all requirements. Our clients who follow our guidance and submit complete, well-prepared applications have a 98% success rate. The key factors are ensuring eligibility, providing complete documentation, and meeting all program requirements.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-base font-light">
            Common questions about Canadian permanent residency applications
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="bg-[#F5F8FA] rounded-lg shadow-sm overflow-hidden border border-gray-200"
            >
              {/* Question */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <span className="text-base font-semibold text-[#202356] pr-4">
                  {faq.question}
                </span>
                <div className="flex-shrink-0">
                  {openIndex === index ? (
                    <Minus size={24} className="text-[#2542D4]" />
                  ) : (
                    <Plus size={24} className="text-[#2542D4]" />
                  )}
                </div>
              </button>

              {/* Answer */}
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 text-gray-600 text-sm font-light leading-relaxed border-t border-gray-200 pt-4">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Additional Help */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6 text-center"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed">
            <strong>Still have questions?</strong> Book a free consultation with our immigration experts to discuss your specific situation and get personalized guidance on your PR journey.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PRFAQSection;
